import { useDebouncedValue } from '@tanstack/react-pacer'

/**
 * Returns a debounced version of `isPlaceholderData` that delays becoming `true` but immediately becomes `false`.
 *
 * Use this with React Query's `keepPreviousData` option to show a loading skeleton only when queries are slow,
 * avoiding a flash of loading state on fast queries while still hiding stale data immediately when fresh data arrives.
 *
 * @param isPlaceholderData - The `isPlaceholderData` value from a React Query result
 * @param staleDelay - Milliseconds to wait before returning `true` (default: 100ms)
 * @returns `true` only after `isPlaceholderData` has been `true` for at least `staleDelay` ms
 *
 * @example
 * ```tsx
 * const { data, isPending, isPlaceholderData } = useQuery({ queryKey, queryFn, placeholderData: keepPreviousData })
 * const isStalePlaceholderData = useIsStalePlaceholderData(isPlaceholderData)
 *
 * if (isPending || isStalePlaceholderData) {
 *   return <Skeleton />
 * }
 * ```
 */
export const useIsStalePlaceholderData = (isPlaceholderData: boolean, staleDelay: number = 100) => {
  // We only debounce `isPlaceholderData`, not `isPending || isPlaceholderData`, because we want the loading state
  // to render immediately on the initial pageload when there is no placeholder data to show.
  const [debouncedIsPlaceholderData] = useDebouncedValue(isPlaceholderData, { wait: staleDelay })

  // AND-ing the debounced value with the non-debounced value allows it to immediately flip back to false without
  // waiting for debouncing. So debouncing only delays the flip from false to true, not from true to false.
  return isPlaceholderData && debouncedIsPlaceholderData
}
